import { useEventCallback } from '@mui/material/utils';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TableInstance } from 'react-table';

import { useEditableState } from '../../../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/edit-table/hooks/use-editable-state';
import { EditTableColumns } from '../../../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/edit-table/types';
import { recalculatePositions } from '../../../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/hooks/recalculatePositions';
import { defaultErpGetRelation } from '../../../../../containers/mask-overlays/mask-overlay/views/erp/components/tab-panels/positions/components/positions-table/defaultErpGetRelation';
import { settings, useSetting } from '../../../../../settings';
import { useErpMask } from '../../hooks/use-erp-mask';
import {
  ErpConversionGroupByType,
  ErpObject,
  GroupedPosition,
  SelectedPosition,
} from '../../types';
import { OnItemsChangedOptions } from '../ErpDialogPositions';
import { useColumnSettings, useColumnsWidth } from '../use-column-settings';

import {
  makeAmountColumn,
  makeArticleNumberColumn,
  makeDescriptionColumn,
  makeDiscountColumn,
  makeNavigateColumn,
  makeNumberColumn,
  makePriceColumn,
  makeSupplierColumn,
  makeTotalPriceColumn,
  makeUnitColumn,
} from './columns-utils';

interface UseGroupedPositionsProps {
  onItemsChanged: (
    positions: SelectedPosition[],
    options: OnItemsChangedOptions
  ) => void;
  supplierId?: string;
  positionList: GroupedPosition[];
  groupByType: ErpConversionGroupByType;
  isEditMode: boolean;
  allData: ErpObject[];
  onRefetch: () => void;
}

export const useGroupedPositions = (props: UseGroupedPositionsProps) => {
  const {
    positionList,
    onItemsChanged,
    supplierId,
    groupByType,
    isEditMode,
    allData,
    onRefetch,
  } = props;

  const { t } = useTranslation();

  const mutateState = useCallback((input: GroupedPosition[]) => {
    const { result } = recalculatePositions(input, false, false);
    return result as GroupedPosition[];
  }, []);

  const { positions, onEditPosition, onCollapsePosition } = useEditableState({
    positions: positionList,
    mutateState,
  });

  const updateWitdth = useColumnsWidth(groupByType);

  const openMask = useErpMask(allData, onRefetch);

  const columns = useMemo(() => {
    const columns: EditTableColumns<GroupedPosition>[] = [
      makeNumberColumn(t),
      makeNavigateColumn(openMask),
      makeDescriptionColumn(
        t,
        245,
        t('COMMON.ERP.DESCRIPTION_CONVERSION_GROUPED')
      ),
      makeArticleNumberColumn(t),
      makeAmountColumn(t, onEditPosition, 70, true, !isEditMode),
      makeUnitColumn(t, 70, onEditPosition, true, !isEditMode),
      makeSupplierColumn(t, onEditPosition, 110, true, !isEditMode),
      makePriceColumn(
        t,
        (x) => x,
        90,
        true,
        isEditMode ? onEditPosition : undefined,
        true
      ),
      makeDiscountColumn(t, onEditPosition, true, !isEditMode),
      makeTotalPriceColumn(t, (x) => x, 90, true),
    ];

    updateWitdth(columns);
    return columns;
  }, [t, openMask, onEditPosition, isEditMode, updateWitdth]);

  const tableInstanceRef = useRef<TableInstance<GroupedPosition>>(null);

  const selectedItems = useRef<string[]>([]);
  const onSelectedItemIdsChange = useEventCallback((items: string[]) => {
    selectedItems.current = items;
    updateSelected();
  });

  // Update list when changing amount
  useEffect(() => {
    updateSelected();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions]);

  const updateSelected = useEventCallback(() => {
    const selectedPositions: SelectedPosition[] = [];
    const original = positionList || [];

    const childPosition: SelectedPosition[] = [];
    original.forEach((originalPos) => {
      const currentPos = positions.find((x) => x.id === originalPos.id);
      if (!currentPos) return;
      const selected = selectedItems.current.includes(currentPos.localId);

      if (!originalPos.isHeader) {
        childPosition.push({ selected, position: currentPos });
        return;
      }

      selectedPositions.push({
        position: currentPos,
        selected,

        newAmount: currentPos.amount,
        newDiscount: currentPos.discount,
        newSinglePrice: currentPos.singlePriceNet,
        newUnit: currentPos.unit,

        oldAmount: originalPos.amountOriginal || originalPos.amount,
        oldDiscount: originalPos.discount,
        oldSinglePrice: originalPos.singlePriceNet,
        oldUnit: originalPos.unit,

        currency: currentPos.currency,
      });
    });
    selectedPositions.forEach((x) => {
      if (x.position.childs?.length > 1) {
        x.childsOriginalData = childPosition.filter(
          (y) => y.position.posId === x.position.id
        );
      }
    });

    onItemsChanged(selectedPositions, { forceEnabled: false, key: supplierId });
  });

  const erpSize = useSetting(settings.erpRowSize());

  const allowedColumns = useMemo(() => {
    return columns.map((x) => x.accessor as string);
  }, [columns]);

  const decorators = useMemo(() => {
    return {
      getRelation: defaultErpGetRelation,
    };
  }, []);

  const { columnSettings, element: resetColumnsElement } = useColumnSettings(
    allowedColumns,
    groupByType
  );

  return {
    tableInstanceRef,
    items: positions,
    onCollapsePosition,
    columns,
    onSelectedItemIdsChange,
    erpSize,
    decorators,
    columnSettings,
    resetColumnsElement,
  };
};
